import React, { useEffect, useState, useCallback } from 'react';
import {
  MonitorDot, Building2, RefreshCw, Download,
  LogIn, LogOut, Users, UserCheck,
} from 'lucide-react';
import { barcodeAPI, establishmentAPI } from '../api/apiService';
import { useAuth, useActiveEstId } from './AuthContext';
import './AttendanceScannerKiosk.css'; // re-uses shared kiosk CSS variables
import './ShiftLiveLog.css';

const REFRESH_MS = 30_000;

export default function ShiftLiveLog() {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [establishments, setEstablishments] = useState([]);
  const [selectedEst, setSelectedEst]       = useState(lockedEstId || '');
  const [logData, setLogData]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [lastRefresh, setLastRefresh]       = useState(null);

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setSelectedEst(lockedEstId);
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) return; // ClientAdmin: already set via lockedEstId
    establishmentAPI.getAll()
      .then(res => {
        const ests = res.establishments || [];
        setEstablishments(ests);
        if (ests.length === 1) setSelectedEst(String(ests[0].id));
      })
      .catch(() => {});
  }, []);

  const fetchLog = useCallback(() => {
    setLoading(true);
    barcodeAPI.today(selectedEst ? Number(selectedEst) : null)
      .then(data => {
        setLogData(data);
        setLastRefresh(new Date());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedEst]);

  // Fetch on mount + on organization change + auto-refresh
  useEffect(() => {
    fetchLog();
    const timer = setInterval(fetchLog, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchLog]);

  const exportCSV = () => {
    if (!logData?.records?.length) return;
    const header = 'Employee ID,Name,Email,Scan Type,Scan Time';
    const rows   = logData.records.map(r =>
      `"${r.employee_id}","${r.employee_name}","${r.email}","${r.scan_type}","${r.scan_time}"`
    );
    const csv  = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href     = URL.createObjectURL(blob);
    link.download = `shift-log-${logData.date}.csv`;
    link.click();
  };

  const records = logData?.records ?? [];

  return (
    <div className="sll-page">

      {/* Header */}
      <div className="ks-header" style={{ marginBottom: '1.25rem' }}>
        <div className="ks-header-icon"><MonitorDot size={20} /></div>
        <div>
          <h1 className="ks-title">Live Shift Log</h1>
          <p className="ks-subtitle">
            Today's real-time check-in / check-out records
            {lastRefresh && (
              <span className="sll-refresh-ts"> · updated {lastRefresh.toLocaleTimeString()}</span>
            )}
          </p>
        </div>
      </div>

      {/* Controls bar */}
      <div className="sll-controls">
        {!lockedEstId && (
        <div className="ks-est-bar" style={{ margin: 0 }}>
          <Building2 size={15} className="ks-est-icon" />
          <label className="ks-est-label">Organization:</label>
          <select
            className="ks-est-select"
            value={selectedEst}
            onChange={e => setSelectedEst(e.target.value)}
          >
            <option value="">All Organizations</option>
            {establishments.map(e => (
              <option key={e.id} value={String(e.id)}>{e.name}</option>
            ))}
          </select>
        </div>
        )}

        <div className="sll-actions">
          <button className="sll-btn-refresh" onClick={fetchLog} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'ks-spin' : ''} />
            Refresh
          </button>
          <button className="sll-btn-export" onClick={exportCSV} disabled={!records.length}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="sll-kpi-row">
        <div className="sll-kpi sll-kpi-scans">
          <Users size={18} />
          <div>
            <span className="sll-kpi-val">{logData?.total_scans ?? '—'}</span>
            <span className="sll-kpi-lbl">Total Scans Today</span>
          </div>
        </div>
        <div className="sll-kpi sll-kpi-in">
          <UserCheck size={18} />
          <div>
            <span className="sll-kpi-val">{logData?.currently_in_count ?? '—'}</span>
            <span className="sll-kpi-lbl">Currently On-Site</span>
          </div>
        </div>
        <div className="sll-kpi sll-kpi-date">
          <MonitorDot size={18} />
          <div>
            <span className="sll-kpi-val">{logData?.date ?? new Date().toLocaleDateString('en-IN')}</span>
            <span className="sll-kpi-lbl">Report Date</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="sll-table-card">
        {loading && !logData ? (
          <div className="sll-loading">
            <div className="mb-spinner" />
            <p>Loading shift log…</p>
          </div>
        ) : records.length === 0 ? (
          <div className="sll-empty">
            <MonitorDot size={40} />
            <p>No scans recorded today for this organization.</p>
          </div>
        ) : (
          <div className="sll-table-wrap">
            <table className="sll-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} className={r.currently_in ? 'sll-row-in' : ''}>
                    <td>
                      <div className="sll-emp-cell">
                        <div className={`sll-emp-dot ${r.scan_type === 'CHECKIN' ? 'sll-dot-in' : 'sll-dot-out'}`} />
                        <div>
                          <p className="sll-emp-name">{r.employee_name}</p>
                          <p className="sll-emp-id">{r.employee_id}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`sll-type-badge ${r.scan_type === 'CHECKIN' ? 'sll-badge-in' : 'sll-badge-out'}`}>
                        {r.scan_type === 'CHECKIN'
                          ? <><LogIn size={11} /> Check-in</>
                          : <><LogOut size={11} /> Check-out</>}
                      </span>
                    </td>
                    <td className="sll-time">
                      {new Date(r.scan_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                    <td>
                      {r.currently_in && (
                        <span className="sll-on-site-badge">On Site</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
